import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Modal from '../Modal/Modal';
import NpsIcon from '@/images/nps.svg?react';
import { NPSExperimentFlagEnum } from '@/core/enums';
import { NPSCommentBlock } from './NPSCommentBlock';
import { NPSNotNow } from './NPSNotNow';
import { useEffect } from 'react';
import { APIEventsEnum, useSendEvent } from '@/hooks';
import { NPS_VIEW_LD_EVENT_KEY } from '@/core/constants';
import { useNPSModal } from './useNPSModal';
import { useLDClient } from 'launchdarkly-react-client-sdk';

export function NPSModal() {
  const { t } = useTranslation();
  const { sendEvent } = useSendEvent();
  const ldClient = useLDClient();

  const {
    isLoading,
    isCommentInputShown,
    score,
    comment,
    setComment,
    isModalOpened,
    onClickNum,
    sendComment,
    closeModal,
    isSkipFeedbackShown,
    setIsSkipFeedbackShown,
    onClickNotNow,
    setIsCommentInputShown,
    setScore,
    setId,
    event,
    onConfirmSkipFeedback,
  } = useNPSModal();

  useEffect(() => {
    if (isModalOpened) {
      ldClient?.track(NPS_VIEW_LD_EVENT_KEY);
      sendEvent(APIEventsEnum.view_cta, {
        block: 'NPSModal',
        event,
        variation: NPSExperimentFlagEnum.popup,
      });
      setIsSkipFeedbackShown(false);
      setIsCommentInputShown(false);
      setScore(undefined);
      setId(undefined);
    }
    // eslint-disable-next-line
  }, [isModalOpened]);

  const renderContent = () => {
    if (isSkipFeedbackShown) {
      return (
        <NPSNotNow
          onClickSkip={onConfirmSkipFeedback}
          onClickShare={() => setIsSkipFeedbackShown(false)}
          className='min-h-[385px] pb-6'
        />
      );
    }
    if (isCommentInputShown) {
      return (
        <NPSCommentBlock
          {...{
            comment,
            isLoading,
            setComment,
            sendComment,
            onClickSkip: closeModal,
          }}
        />
      );
    }
    return (
      <div className='p-6'>
        <div className='flex justify-center'>
          <NpsIcon />
        </div>
        <h2 className='mx-auto mb-4 max-w-[420px] text-center text-h4 font-extrabold leading-6'>
          {t('How likely are you to recommend countX to others?')}
        </h2>
        <div>
          <div className='mb-4 flex flex-wrap items-center justify-between gap-x-1'>
            <div className='flex items-center gap-x-1'>
              {[...Array(11)].map((_, i) => (
                <button
                  key={i}
                  className={`flex h-12 w-12 cursor-pointer items-center justify-center rounded-2xl border text-center font-bold leading-none transition-colors ${
                    score === i
                      ? 'border-green bg-green text-white'
                      : 'border-gray-500 bg-white text-dark'
                  }`}
                  onClick={() => onClickNum(i)}
                  disabled={isLoading}
                  data-testid={`nps-score-${i}`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
          <div className='mb-10 flex items-center justify-between text-gray-700'>
            <span>{t('Not likely at all')}</span>
            <Button
              className='min-w-[155px] !rounded-2xl'
              styling='outline'
              disabled={isLoading}
              onClick={onClickNotNow}
              data-testid='not-now-btn'
            >
              {t('Not now')}
            </Button>
            <span>{t('Extremely likely')}</span>
          </div>
          <div className='text-center text-gray-700'>
            {t(
              'This form will disappear for 90 days after you share your feedback.'
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpened={isModalOpened} close={undefined} name='NPSModal'>
      {renderContent()}
    </Modal>
  );
}
